// Die Feldliste der Suche (ST-SEA-002, „PQL S1“/„S3“; E7 a: Felder sind Code
// mit Version). Seit S3 liefert der Server Namen, Aliase und Mehrwertigkeit
// (GET /api/party-search-fields, uebernommen mit `uebernimmFeldliste`);
// FIELDS bleibt die Fassung 1 und gilt, bis die Liste da ist oder wenn sie
// nicht kommt. Die Formgrenzen stehen nicht als Abschrift hier, sondern aus
// parties.ts importiert, das sie gegen den Server prueft.

import { ROLE_LABELS, roleLabelSpellings } from '../../context-roles.ts';
import { auswahlWort, type HausArt } from '../../haus-art.ts';
import {
  LIFECYCLE_LABELS,
  MAXIMUM_LOCALITY_LENGTH,
  MAXIMUM_POSTAL_CODE_PREFIX_LENGTH,
  MINIMUM_LOCALITY_LENGTH,
} from '../../parties.ts';

export type FieldKey = 'stichwort' | 'haus' | 'rolle' | 'status' | 'ort' | 'plz';

export interface FieldDefinition {
  readonly key: FieldKey;
  /** Wie der Satz die Angabe nennt: „Stichwort“, „PLZ“. */
  readonly label: string;
  /** Kanonischer Name zuerst, dann Aliase; alle klein. */
  readonly names: readonly string[];
  /** Traegt mehrere Werte, die die Facette mit ODER verknuepft. */
  readonly multiValued: boolean;
  /** Offene Werteliste: ein Ausschluss ist nicht das Komplement einer festen Liste. */
  readonly open: boolean;
}

export const FIELDS: readonly FieldDefinition[] = Object.freeze([
  { key: 'stichwort', label: 'Stichwort', names: ['stichwort'], multiValued: true, open: true },
  { key: 'haus', label: 'Verlag', names: ['haus', 'verlag'], multiValued: false, open: true },
  { key: 'rolle', label: 'Rolle', names: ['rolle'], multiValued: true, open: false },
  { key: 'status', label: 'Zustand', names: ['status'], multiValued: true, open: false },
  { key: 'ort', label: 'Ort', names: ['ort'], multiValued: false, open: true },
  { key: 'plz', label: 'PLZ', names: ['plz'], multiValued: false, open: true },
]);

/**
 * Felder, die die Referenzfaelle aus 8.6.1 nennen und spaetere Schnitte
 * bringen (S5). Sie werden erkannt, damit `hat:E-Mail` nicht still Freitext
 * wird, und mit „kommt spaeter“ abgelehnt.
 */
export const LATER_FIELD_NAMES: readonly string[] = Object.freeze([
  'hat',
  'has',
  'angelegt',
  'erstellt',
  'name',
  'kennung',
  'art',
  'sammlung',
  'geaendert',
  'geändert',
  'letzte_aktivitaet',
]);

export const POSTAL_CODE_PATTERN = new RegExp(`^[0-9]{1,${MAXIMUM_POSTAL_CODE_PREFIX_LENGTH}}$`);
export { MAXIMUM_LOCALITY_LENGTH, MINIMUM_LOCALITY_LENGTH };

/**
 * Faltung fuer Vergleiche von Feldnamen und Werten der geschlossenen Listen:
 * Gross- und Kleinschreibung, Umlaute und Leerraum spielen keine Rolle --
 * dieselbe Zusage, die der Feldhinweis der Suche macht.
 */
export function fold(value: string): string {
  return value
    .toLocaleLowerCase('de-DE')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

let aktiveFelder: readonly FieldDefinition[] = FIELDS;

/** Die Felder, die gerade gelten: die des Servers, sonst Fassung 1. */
export function felder(): readonly FieldDefinition[] {
  return hausArten === undefined ? aktiveFelder : aktiveFelder.map(mitHausWort);
}

/**
 * Das Feld `haus:` heisst nach der Art der eigenen Haeuser (M4/M7): "Verlag",
 * "Holding", "Vertrieb", bei gemischter Art "Gesellschaft". Die Feldliste des
 * Servers kennt die Haeuser der Person nicht; das Wort kommt deshalb aus den
 * Suchoptionen, sobald sie geladen sind, und bis dahin aus dem Server.
 */
let hausArten: readonly (HausArt | null | undefined)[] | undefined;

export function uebernimmHausArten(arten: readonly (HausArt | null | undefined)[]): void {
  hausArten = arten;
}

function mitHausWort(definition: FieldDefinition): FieldDefinition {
  return definition.key === 'haus' && hausArten !== undefined ? { ...definition, label: auswahlWort(hausArten) } : definition;
}

/** Die Felder, die die Suche heute kennt; ein unbekannter Schluessel vom Server hat hier keinen Baustein. */
export const FIELD_KEYS: readonly FieldKey[] = Object.freeze(FIELDS.map((definition) => definition.key));

/**
 * Uebernimmt die Feldliste des Servers. Nur Felder, fuer die das Web einen
 * Baustein hat (FIELD_KEYS); jedes behaelt mindestens seinen kanonischen
 * Namen. Fehlt ein bekanntes Feld, gilt fuer es Fassung 1 weiter.
 */
export function uebernimmFeldliste(server: readonly { readonly key: string; readonly label: string; readonly aliases: readonly string[]; readonly multiValued: boolean; readonly openValues: boolean }[]): void {
  const vomServer = new Map(server.filter((feld) => (FIELD_KEYS as readonly string[]).includes(feld.key)).map((feld) => [feld.key, feld]));
  aktiveFelder = Object.freeze(
    FIELDS.map((fassung1) => {
      const feld = vomServer.get(fassung1.key);
      if (feld === undefined) return fassung1;
      return Object.freeze({
        key: fassung1.key,
        label: feld.label,
        names: Object.freeze([fassung1.key, ...feld.aliases.map((alias) => alias.toLocaleLowerCase('de-DE')).filter((alias) => alias !== fassung1.key)]),
        multiValued: feld.multiValued,
        open: feld.openValues,
      });
    }),
  );
}

/** Fuer Proben: zurueck auf Fassung 1. */
export function feldlisteZuruecksetzen(): void {
  aktiveFelder = FIELDS;
  hausArten = undefined;
}

export function fieldByName(name: string): FieldDefinition | undefined {
  const folded = fold(name);
  const definition = aktiveFelder.find((kandidat) => kandidat.names.some((candidate) => fold(candidate) === folded));
  return definition === undefined ? undefined : mitHausWort(definition);
}

export function isLaterField(name: string): boolean {
  const folded = fold(name);
  return LATER_FIELD_NAMES.some((candidate) => fold(candidate) === folded);
}

/** Damerau-Levenshtein mit benachbarter Vertauschung („stihcwort“). */
export function editDistance(a: string, b: string): number {
  const rows = a.length + 1;
  const columns = b.length + 1;
  const d: number[][] = Array.from({ length: rows }, () => new Array<number>(columns).fill(0));
  for (let i = 0; i < rows; i += 1) d[i]![0] = i;
  for (let j = 0; j < columns; j += 1) d[0]![j] = j;
  for (let i = 1; i < rows; i += 1) {
    for (let j = 1; j < columns; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let best = Math.min(d[i - 1]![j]! + 1, d[i]![j - 1]! + 1, d[i - 1]![j - 1]! + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        best = Math.min(best, d[i - 2]![j - 2]! + 1);
      }
      d[i]![j] = best;
    }
  }
  return d[a.length]![b.length]!;
}

/**
 * Wie weit ein getipptes Wort von einem Feldnamen abweichen darf, damit es
 * als Tippfehler gilt und blockiert. Kurze Woerter sind fast immer etwas
 * anderes („ab: 10 Uhr“) und werden Freitext.
 */
function allowedDistance(length: number): number {
  if (length >= 5) return 2;
  if (length >= 3) return 1;
  return 0;
}

/** Der naechste Feldname zu einem unbekannten Wort, oder nichts. */
export function similarFieldName(name: string): string | undefined {
  const folded = fold(name);
  const limit = allowedDistance(folded.length);
  let best: { readonly name: string; readonly distance: number } | undefined;
  for (const definition of aktiveFelder) {
    for (const candidate of definition.names) {
      const distance = editDistance(folded, candidate);
      if (distance > 0 && distance <= limit && (best === undefined || distance < best.distance)) {
        best = { name: candidate, distance };
      }
    }
  }
  return best?.name;
}

/** Die Rollen in Worten als Werte: „Journalistin“, „Journalist“, „journalist“. */
function roleSpellings(): ReadonlyMap<string, string> {
  const spellings = new Map<string, string>();
  for (const [code, label] of Object.entries(ROLE_LABELS)) {
    spellings.set(fold(code), code);
    spellings.set(fold(label), code);
    for (const part of roleLabelSpellings(label)) {
      spellings.set(fold(part), code);
    }
  }
  return spellings;
}

const ROLE_SPELLINGS = roleSpellings();

export function roleCodeFor(value: string): string | undefined {
  return ROLE_SPELLINGS.get(fold(value));
}

/** Die naechste Schreibweise einer Rolle zu einem unbekannten Wort, oder nichts. */
export function similarRoleSpelling(value: string): string | undefined {
  const folded = fold(value);
  const limit = allowedDistance(folded.length);
  let best: { readonly spelling: string; readonly distance: number } | undefined;
  for (const label of Object.values(ROLE_LABELS)) {
    for (const part of roleLabelSpellings(label)) {
      const distance = editDistance(folded, fold(part));
      if (distance <= limit && (best === undefined || distance < best.distance)) {
        best = { spelling: part, distance };
      }
    }
  }
  return best?.spelling;
}

/** Zustandscodes zu ihren Worten, nur die drei, die die Suche kennt. */
export const STATUS_CODES: readonly string[] = Object.freeze(['draft', 'active', 'inactive']);

export function statusCodeFor(value: string): string | undefined {
  const folded = fold(value);
  return STATUS_CODES.find((code) => fold(code) === folded || fold(LIFECYCLE_LABELS[code] ?? code) === folded);
}
